/**
 * UI state management for Supernal TTS Widget
 */

import { formatTime } from '../core/text-processor';

export class UIStateManager {
  /**
   * Set button visual state (loading, playing, paused, error, ready)
   */
  public setButtonState(button: HTMLButtonElement, state: string): void {
    button.classList.remove('supernal-tts-loading', 'supernal-tts-playing', 'supernal-tts-paused', 'supernal-tts-error');

    const playIcon = button.querySelector<SVGElement>('.supernal-tts-play-icon');
    const circleIcon = button.querySelector<HTMLSpanElement>('.supernal-tts-play-icon-circle');
    const text = button.querySelector<HTMLSpanElement>('.supernal-tts-text');
    const isCircleButton = button.classList.contains('supernal-tts-play-circle');

    switch (state) {
      case 'loading':
        button.classList.add('supernal-tts-loading');
        button.disabled = true;
        if (circleIcon) {
          circleIcon.textContent = '...';
        } else if (!isCircleButton && text) {
          text.textContent = 'Loading...';
        }
        break;

      case 'playing':
        button.classList.add('supernal-tts-playing');
        button.disabled = false;
        if (circleIcon) {
          circleIcon.textContent = '⏸';
        } else if (playIcon) {
          playIcon.innerHTML = '<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>';
        }
        if (text) text.textContent = 'Pause';
        break;

      case 'paused':
        button.classList.add('supernal-tts-paused');
        button.disabled = false;
        if (circleIcon) {
          circleIcon.textContent = '▶';
        } else if (playIcon) {
          playIcon.innerHTML = '<path d="M8 5v14l11-7z"/>';
        }
        if (text) text.textContent = 'Resume';
        break;

      case 'error':
        button.classList.add('supernal-tts-error');
        button.disabled = false;
        if (circleIcon) {
          circleIcon.textContent = '!';
        } else if (playIcon) {
          playIcon.innerHTML = '<path d="M12 2L2 7v10c0 5.55 3.84 9.95 9 11 5.16-1.05 9-5.45 9-11V7l-10-5z"/>';
        }
        if (text) text.textContent = 'Error';
        break;

      default: // ready
        button.disabled = false;
        if (circleIcon) {
          circleIcon.textContent = '▶';
        } else if (playIcon) {
          playIcon.innerHTML = '<path d="M8 5v14l11-7z"/>';
        }
        if (text) text.textContent = 'Listen';
        break;
    }
  }

  /**
   * Show error tooltip for a button
   */
  public showError(button: HTMLButtonElement, message: string): void {
    const tooltip = document.createElement('div');
    tooltip.className = 'supernal-tts-error-tooltip';

    if (message.includes('development mode')) {
      tooltip.textContent = message;
    } else if (message.includes('Failed to fetch')) {
      tooltip.textContent = 'Service unavailable (dev mode)';
    } else {
      tooltip.textContent = 'Audio generation failed';
    }

    button.parentNode?.appendChild(tooltip);

    setTimeout(() => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    }, 3000);
  }

  /**
   * Update progress bar during playback
   */
  public updateProgressBar(audio: HTMLAudioElement, progressBar: HTMLElement): void {
    if (!audio || !progressBar) return;

    const slider = progressBar.querySelector<HTMLInputElement>('.supernal-tts-progress-slider');
    const currentTimeSpan = progressBar.querySelector<HTMLElement>('.current');
    const durationSpan = progressBar.querySelector<HTMLElement>('.duration');

    if (slider && !isNaN(audio.duration)) {
      const percentage = (audio.currentTime / audio.duration) * 100;
      slider.value = percentage.toString();
    }

    if (currentTimeSpan) {
      currentTimeSpan.textContent = formatTime(audio.currentTime);
    }

    if (durationSpan && !isNaN(audio.duration)) {
      durationSpan.textContent = formatTime(audio.duration);
    }
  }
}
